import {
  adminLeadEngagementPageSizes,
  crmTagDisplayLabelMaxLength,
  crmTaskStatuses,
  crmTaskTitleMaxLength,
  leadNoteBodyMaxLength,
  taskReminderStatuses,
  type AdminLeadEngagementCursor,
  type AdminLeadNote,
  type AdminLeadNoteCreateOutcome,
  type AdminLeadNoteCreateRequest,
  type AdminLeadNotePage,
  type AdminLeadTag,
  type AdminLeadTagMutationOutcome,
  type AdminLeadTagMutationRequest,
  type AdminLeadTagPage,
  type AdminLeadTask,
  type AdminLeadTaskMutationOutcome,
  type AdminLeadTaskMutationRequest,
  type AdminLeadTaskPage,
  type AdminLeadTaskReminder,
  type AdminLeadTaskReminderMutationOutcome,
  type AdminLeadTaskReminderMutationRequest,
  type AdminLeadTaskReminderPage,
  type LeadId,
} from "@whitehaven/contracts";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";
import { isBoundedOpaqueCrmCursor } from "./opaque-cursor-model";

export const MAX_CRM_ENGAGEMENT_REVISION = 2_147_483_647;

export const crmTaskStatusLabels = {
  OPEN: "Open",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
} as const;

export const taskReminderStatusLabels = {
  PENDING: "Scheduled marker",
  CANCELLED: "Cancelled",
} as const;

const crmTaskStatusSet: ReadonlySet<string> = new Set(crmTaskStatuses);
const reminderStatusSet: ReadonlySet<string> = new Set(taskReminderStatuses);
const safeOpaqueIdPattern = /^[A-Za-z0-9_-]{8,128}$/;
const prohibitedSingleLineCharacters =
  /[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}]/u;
const prohibitedMultilineCharacters =
  /[\u0000-\u0009\u000b-\u001f\u007f\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}]/u;

function hasExactOwnKeys(
  value: object,
  expectedKeys: readonly string[],
): boolean {
  const actualKeys = Reflect.ownKeys(value);
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every(
      (key) => typeof key === "string" && expectedKeys.includes(key),
    )
  );
}

export type AdminLeadEngagementPageState<T> =
  | { readonly kind: "unavailable" }
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly items: readonly T[];
      readonly nextCursor: AdminLeadEngagementCursor | null;
      readonly consumedCursors: readonly AdminLeadEngagementCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
      readonly notice: "REFRESH_FAILED" | "LOAD_MORE_FAILED" | null;
    };

export function isCanonicalEngagementTimestamp(
  value: unknown,
): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }

  const date = new Date(value);
  return Number.isFinite(date.getTime()) && date.toISOString() === value;
}

export function isSafeEngagementId(value: unknown): value is string {
  return typeof value === "string" && safeOpaqueIdPattern.test(value);
}

export function isCrmEngagementRevision(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_CRM_ENGAGEMENT_REVISION
  );
}

function normalizeText(
  value: unknown,
  maxLength: number,
  multiline: boolean,
): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.replace(/\r\n?/g, "\n").trim().normalize("NFC");
  if (
    normalized.length < 1 ||
    normalized.length > maxLength ||
    (multiline
      ? prohibitedMultilineCharacters.test(normalized)
      : prohibitedSingleLineCharacters.test(normalized))
  ) {
    return null;
  }
  return normalized;
}

export function normalizeCrmTagDisplayLabel(value: unknown): string | null {
  return normalizeText(value, crmTagDisplayLabelMaxLength, false);
}

export function normalizeCrmTaskTitle(value: unknown): string | null {
  return normalizeText(value, crmTaskTitleMaxLength, false);
}

export function normalizeLeadNoteBody(value: unknown): string | null {
  return normalizeText(value, leadNoteBodyMaxLength, true);
}

export function hawaiiLocalDateTimeToCanonicalUtc(
  value: string,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}:00-10:00`);
  if (!Number.isFinite(parsed.getTime())) return null;

  const reconstructed = new Date(parsed.getTime() - 10 * 60 * 60 * 1_000)
    .toISOString()
    .slice(0, 16);
  return reconstructed === value ? parsed.toISOString() : null;
}

const hawaiiDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

export function formatHawaiiEngagementDate(value: string): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? `${hawaiiDateFormatter.format(date)} HST`
    : null;
}

function hasValidPageEnvelope(
  value: unknown,
  leadId: LeadId,
): value is {
  readonly leadId: LeadId;
  readonly items: readonly unknown[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
} {
  if (typeof value !== "object" || value === null) return false;
  if (!hasExactOwnKeys(value, ["leadId", "items", "nextCursor"])) {
    return false;
  }
  const page = value as {
    readonly leadId?: unknown;
    readonly items?: unknown;
    readonly nextCursor?: unknown;
  };
  return (
    page.leadId === leadId &&
    Array.isArray(page.items) &&
    page.items.length <= adminLeadEngagementPageSizes[0] &&
    (page.nextCursor === null || isBoundedOpaqueCrmCursor(page.nextCursor)) &&
    !(page.items.length === 0 && page.nextCursor !== null)
  );
}

export function isAdminLeadTag(value: unknown): value is AdminLeadTag {
  if (typeof value !== "object" || value === null) return false;
  if (
    !hasExactOwnKeys(value, [
      "assignmentId",
      "leadId",
      "tagId",
      "displayLabel",
      "revision",
      "attachedAt",
      "updatedAt",
    ])
  ) {
    return false;
  }
  const item = value as AdminLeadTag;
  return (
    isSafeEngagementId(item.assignmentId) &&
    isSafeEngagementId(item.tagId) &&
    typeof item.leadId === "string" &&
    isSafeLeadIdPathSegment(item.leadId) &&
    normalizeCrmTagDisplayLabel(item.displayLabel) === item.displayLabel &&
    item.revision === 0 &&
    isCanonicalEngagementTimestamp(item.attachedAt) &&
    isCanonicalEngagementTimestamp(item.updatedAt) &&
    item.attachedAt === item.updatedAt
  );
}

export function isAdminLeadTask(value: unknown): value is AdminLeadTask {
  if (typeof value !== "object" || value === null) return false;
  if (
    !hasExactOwnKeys(value, [
      "taskId",
      "leadId",
      "title",
      "dueAt",
      "status",
      "revision",
      "createdAt",
      "updatedAt",
    ])
  ) {
    return false;
  }
  const item = value as AdminLeadTask;
  return (
    isSafeEngagementId(item.taskId) &&
    typeof item.leadId === "string" &&
    isSafeLeadIdPathSegment(item.leadId) &&
    normalizeCrmTaskTitle(item.title) === item.title &&
    isCanonicalEngagementTimestamp(item.dueAt) &&
    typeof item.status === "string" &&
    crmTaskStatusSet.has(item.status) &&
    isCrmEngagementRevision(item.revision) &&
    isCanonicalEngagementTimestamp(item.createdAt) &&
    isCanonicalEngagementTimestamp(item.updatedAt) &&
    item.createdAt <= item.updatedAt &&
    item.dueAt > item.createdAt &&
    (item.status === "OPEN"
      ? item.revision === 0 && item.updatedAt === item.createdAt
      : item.revision === 1 && item.updatedAt > item.createdAt)
  );
}

export function isAdminLeadTaskReminder(
  value: unknown,
): value is AdminLeadTaskReminder {
  if (typeof value !== "object" || value === null) return false;
  if (
    !hasExactOwnKeys(value, [
      "reminderId",
      "leadId",
      "taskId",
      "scheduledFor",
      "status",
      "revision",
      "createdAt",
      "updatedAt",
    ])
  ) {
    return false;
  }
  const item = value as AdminLeadTaskReminder;
  return (
    isSafeEngagementId(item.reminderId) &&
    typeof item.leadId === "string" &&
    isSafeLeadIdPathSegment(item.leadId) &&
    isSafeEngagementId(item.taskId) &&
    isCanonicalEngagementTimestamp(item.scheduledFor) &&
    typeof item.status === "string" &&
    reminderStatusSet.has(item.status) &&
    isCrmEngagementRevision(item.revision) &&
    isCanonicalEngagementTimestamp(item.createdAt) &&
    isCanonicalEngagementTimestamp(item.updatedAt) &&
    item.createdAt <= item.updatedAt &&
    item.scheduledFor > item.createdAt &&
    (item.status === "PENDING"
      ? item.revision === 0 && item.updatedAt === item.createdAt
      : item.revision === 1 && item.updatedAt > item.createdAt)
  );
}

export function isAdminLeadNote(value: unknown): value is AdminLeadNote {
  if (typeof value !== "object" || value === null) return false;
  if (
    !hasExactOwnKeys(value, [
      "noteId",
      "leadId",
      "bodyText",
      "supersedesNoteId",
      "createdAt",
    ])
  ) {
    return false;
  }
  const item = value as AdminLeadNote;
  return (
    isSafeEngagementId(item.noteId) &&
    typeof item.leadId === "string" &&
    isSafeLeadIdPathSegment(item.leadId) &&
    normalizeLeadNoteBody(item.bodyText) === item.bodyText &&
    (item.supersedesNoteId === null ||
      isSafeEngagementId(item.supersedesNoteId)) &&
    item.supersedesNoteId !== item.noteId &&
    isCanonicalEngagementTimestamp(item.createdAt)
  );
}

function uniqueItems<T>(
  items: readonly T[],
  identity: (item: T) => string,
): boolean {
  const seen = new Set<string>();
  return items.every((item) => {
    const key = identity(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function isAdminLeadTagPageConsistent(
  value: unknown,
  leadId: LeadId,
): value is AdminLeadTagPage {
  if (!hasValidPageEnvelope(value, leadId)) return false;
  const page = value as AdminLeadTagPage;
  return (
    page.items.every(
      (item) => isAdminLeadTag(item) && item.leadId === leadId,
    ) &&
    uniqueItems(page.items, (item) => item.assignmentId) &&
    uniqueItems(page.items, (item) => item.tagId)
  );
}

export function isAdminLeadTaskPageConsistent(
  value: unknown,
  leadId: LeadId,
): value is AdminLeadTaskPage {
  if (!hasValidPageEnvelope(value, leadId)) return false;
  const page = value as AdminLeadTaskPage;
  return (
    page.items.every(
      (item) => isAdminLeadTask(item) && item.leadId === leadId,
    ) && uniqueItems(page.items, (item) => item.taskId)
  );
}

export function isAdminLeadTaskReminderPageConsistent(
  value: unknown,
  leadId: LeadId,
): value is AdminLeadTaskReminderPage {
  if (!hasValidPageEnvelope(value, leadId)) return false;
  const page = value as AdminLeadTaskReminderPage;
  return (
    page.items.every(
      (item) => isAdminLeadTaskReminder(item) && item.leadId === leadId,
    ) &&
    uniqueItems(page.items, (item) => item.reminderId) &&
    hasAtMostOnePendingReminderPerTask(page.items)
  );
}

function hasAtMostOnePendingReminderPerTask(
  items: readonly AdminLeadTaskReminder[],
): boolean {
  const pendingTaskIds = new Set<string>();
  return items.every((item) => {
    if (item.status !== "PENDING") return true;
    if (pendingTaskIds.has(item.taskId)) return false;
    pendingTaskIds.add(item.taskId);
    return true;
  });
}

export function isAdminLeadNotePageConsistent(
  value: unknown,
  leadId: LeadId,
): value is AdminLeadNotePage {
  if (!hasValidPageEnvelope(value, leadId)) return false;
  const page = value as AdminLeadNotePage;
  return (
    page.items.every(
      (item) => isAdminLeadNote(item) && item.leadId === leadId,
    ) && uniqueItems(page.items, (item) => item.noteId)
  );
}

function mergeUnique<T>(
  current: readonly T[],
  incoming: readonly T[],
  identity: (item: T) => string,
  compare: (left: T, right: T) => number,
): readonly T[] | null {
  const merged = new Map(
    current.map((item) => [identity(item), item] as const),
  );
  for (const item of incoming) {
    if (merged.has(identity(item))) return null;
    merged.set(identity(item), item);
  }
  return [...merged.values()].sort(compare);
}

export function mergeAdminLeadTags(
  current: readonly AdminLeadTag[],
  incoming: readonly AdminLeadTag[],
): readonly AdminLeadTag[] | null {
  const merged = mergeUnique(
    current,
    incoming,
    (item) => item.assignmentId,
    (left, right) =>
      left.displayLabel.localeCompare(right.displayLabel, "en", {
        sensitivity: "base",
      }) || left.assignmentId.localeCompare(right.assignmentId),
  );
  return merged && uniqueItems(merged, (item) => item.tagId) ? merged : null;
}

export function mergeAdminLeadTasks(
  current: readonly AdminLeadTask[],
  incoming: readonly AdminLeadTask[],
): readonly AdminLeadTask[] | null {
  return mergeUnique(
    current,
    incoming,
    (item) => item.taskId,
    (left, right) =>
      left.dueAt.localeCompare(right.dueAt) ||
      left.taskId.localeCompare(right.taskId),
  );
}

export function mergeAdminLeadTaskReminders(
  current: readonly AdminLeadTaskReminder[],
  incoming: readonly AdminLeadTaskReminder[],
): readonly AdminLeadTaskReminder[] | null {
  const merged = mergeUnique(
    current,
    incoming,
    (item) => item.reminderId,
    (left, right) =>
      left.scheduledFor.localeCompare(right.scheduledFor) ||
      left.reminderId.localeCompare(right.reminderId),
  );
  return merged && hasAtMostOnePendingReminderPerTask(merged) ? merged : null;
}

export function mergeAdminLeadNotes(
  current: readonly AdminLeadNote[],
  incoming: readonly AdminLeadNote[],
): readonly AdminLeadNote[] | null {
  return mergeUnique(
    current,
    incoming,
    (item) => item.noteId,
    (left, right) =>
      right.createdAt.localeCompare(left.createdAt) ||
      right.noteId.localeCompare(left.noteId),
  );
}

function replaceById<T>(
  items: readonly T[],
  next: T,
  identity: (item: T) => string,
  compare: (left: T, right: T) => number,
): readonly T[] | null {
  const target = identity(next);
  if (!items.some((item) => identity(item) === target)) return null;
  return items
    .map((item) => (identity(item) === target ? next : item))
    .sort(compare);
}

export function applyConfirmedAdminLeadTagMutation(
  items: readonly AdminLeadTag[],
  request: AdminLeadTagMutationRequest,
  value: unknown,
): readonly AdminLeadTag[] | null {
  if (typeof value !== "object" || value === null) return null;
  const outcome = value as AdminLeadTagMutationOutcome;
  if (outcome.kind !== "confirmed") return null;

  if (request.action === "ADD") {
    if (
      !hasExactOwnKeys(value, ["kind", "action", "item"]) ||
      outcome.action !== "ADDED" ||
      !isAdminLeadTag(outcome.item) ||
      outcome.item.leadId !== request.leadId ||
      outcome.item.displayLabel !== request.displayLabel ||
      outcome.item.revision !== 0 ||
      items.some(
        (item) =>
          item.assignmentId === outcome.item.assignmentId ||
          item.tagId === outcome.item.tagId,
      )
    ) {
      return null;
    }
    return mergeAdminLeadTags(items, [outcome.item]);
  }

  const current = items.find(
    (item) => item.assignmentId === request.assignmentId,
  );
  if (
    !hasExactOwnKeys(value, [
      "kind",
      "action",
      "assignmentId",
      "revision",
      "updatedAt",
    ]) ||
    !current ||
    current.revision !== request.expectedRevision ||
    current.updatedAt !== request.expectedUpdatedAt ||
    outcome.action !== "REMOVED" ||
    outcome.assignmentId !== request.assignmentId ||
    outcome.revision !== request.expectedRevision + 1 ||
    !isCanonicalEngagementTimestamp(outcome.updatedAt) ||
    outcome.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }
  return items.filter((item) => item.assignmentId !== request.assignmentId);
}

export function applyConfirmedAdminLeadTaskMutation(
  items: readonly AdminLeadTask[],
  request: AdminLeadTaskMutationRequest,
  value: unknown,
): readonly AdminLeadTask[] | null {
  if (typeof value !== "object" || value === null) return null;
  if (!hasExactOwnKeys(value, ["kind", "action", "item"])) return null;
  const outcome = value as AdminLeadTaskMutationOutcome;
  if (
    outcome.kind !== "confirmed" ||
    !isAdminLeadTask(outcome.item) ||
    outcome.item.leadId !== request.leadId
  ) {
    return null;
  }

  if (request.action === "CREATE") {
    if (
      outcome.action !== "CREATED" ||
      outcome.item.title !== request.title ||
      outcome.item.dueAt !== request.dueAt ||
      outcome.item.status !== "OPEN" ||
      outcome.item.revision !== 0 ||
      items.some((item) => item.taskId === outcome.item.taskId)
    ) {
      return null;
    }
    return mergeAdminLeadTasks(items, [outcome.item]);
  }

  const current = items.find((item) => item.taskId === request.taskId);
  const expectedStatus =
    request.action === "COMPLETE" ? "COMPLETED" : "CANCELLED";
  if (
    !current ||
    current.status !== "OPEN" ||
    current.revision !== request.expectedRevision ||
    current.updatedAt !== request.expectedUpdatedAt ||
    outcome.action !==
      (request.action === "COMPLETE" ? "COMPLETED" : "CANCELLED") ||
    outcome.item.taskId !== request.taskId ||
    outcome.item.title !== current.title ||
    outcome.item.dueAt !== current.dueAt ||
    outcome.item.createdAt !== current.createdAt ||
    outcome.item.status !== expectedStatus ||
    outcome.item.revision !== request.expectedRevision + 1 ||
    outcome.item.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }
  return replaceById(
    items,
    outcome.item,
    (item) => item.taskId,
    (left, right) =>
      left.dueAt.localeCompare(right.dueAt) ||
      left.taskId.localeCompare(right.taskId),
  );
}

export function applyConfirmedAdminLeadReminderMutation(
  items: readonly AdminLeadTaskReminder[],
  tasks: readonly AdminLeadTask[],
  request: AdminLeadTaskReminderMutationRequest,
  value: unknown,
): readonly AdminLeadTaskReminder[] | null {
  if (typeof value !== "object" || value === null) return null;
  if (!hasExactOwnKeys(value, ["kind", "action", "item"])) return null;
  const outcome = value as AdminLeadTaskReminderMutationOutcome;
  if (
    outcome.kind !== "confirmed" ||
    !isAdminLeadTaskReminder(outcome.item) ||
    outcome.item.leadId !== request.leadId
  ) {
    return null;
  }

  if (request.action === "SCHEDULE") {
    const task = tasks.find((item) => item.taskId === request.taskId);
    if (
      !task ||
      task.leadId !== request.leadId ||
      task.status !== "OPEN" ||
      !isReminderScheduleConsistentWithTask(task, request.scheduledFor) ||
      outcome.action !== "SCHEDULED" ||
      outcome.item.taskId !== request.taskId ||
      outcome.item.scheduledFor !== request.scheduledFor ||
      outcome.item.status !== "PENDING" ||
      outcome.item.revision !== 0 ||
      items.some((item) => item.reminderId === outcome.item.reminderId)
    ) {
      return null;
    }
    return mergeAdminLeadTaskReminders(items, [outcome.item]);
  }

  const current = items.find((item) => item.reminderId === request.reminderId);
  if (
    !current ||
    current.status !== "PENDING" ||
    current.revision !== request.expectedRevision ||
    current.updatedAt !== request.expectedUpdatedAt ||
    outcome.action !== "CANCELLED" ||
    outcome.item.reminderId !== request.reminderId ||
    outcome.item.taskId !== current.taskId ||
    outcome.item.scheduledFor !== current.scheduledFor ||
    outcome.item.createdAt !== current.createdAt ||
    outcome.item.status !== "CANCELLED" ||
    outcome.item.revision !== request.expectedRevision + 1 ||
    outcome.item.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }
  return replaceById(
    items,
    outcome.item,
    (item) => item.reminderId,
    (left, right) =>
      left.scheduledFor.localeCompare(right.scheduledFor) ||
      left.reminderId.localeCompare(right.reminderId),
  );
}

export function isReminderScheduleConsistentWithTask(
  task: AdminLeadTask,
  scheduledFor: string,
  now?: Date,
): boolean {
  if (
    !isAdminLeadTask(task) ||
    task.status !== "OPEN" ||
    !isCanonicalEngagementTimestamp(scheduledFor) ||
    (now !== undefined && !Number.isFinite(now.getTime()))
  ) {
    return false;
  }
  return (
    scheduledFor > task.createdAt &&
    (now === undefined || scheduledFor > now.toISOString()) &&
    scheduledFor <= task.dueAt
  );
}

export function applyConfirmedAdminLeadNoteCreation(
  items: readonly AdminLeadNote[],
  request: AdminLeadNoteCreateRequest,
  value: unknown,
): readonly AdminLeadNote[] | null {
  if (typeof value !== "object" || value === null) return null;
  if (!hasExactOwnKeys(value, ["kind", "item"])) return null;
  const outcome = value as AdminLeadNoteCreateOutcome;
  if (
    outcome.kind !== "confirmed" ||
    !isAdminLeadNote(outcome.item) ||
    outcome.item.leadId !== request.leadId ||
    outcome.item.bodyText !== request.bodyText ||
    outcome.item.supersedesNoteId !== request.supersedesNoteId ||
    items.some((item) => item.noteId === outcome.item.noteId)
  ) {
    return null;
  }
  return mergeAdminLeadNotes(items, [outcome.item]);
}

export function isEngagementFailureOutcome(
  value: unknown,
): value is { readonly kind: "conflict" | "rejected" } {
  return (
    typeof value === "object" &&
    value !== null &&
    hasExactOwnKeys(value, ["kind"]) &&
    ((value as { readonly kind?: unknown }).kind === "conflict" ||
      (value as { readonly kind?: unknown }).kind === "rejected")
  );
}
